/**
 * Lightweight cache for Firebase Storage download URLs.
 *
 * We don't cache the image bytes here—that's the browser's job. Instead we cache the
 * resolved download URL for each gs:// path so we can:
 *   1. Skip repeated calls to getDownloadURL (which hits the Storage API each time).
 *   2. Reuse the same URL so the browser's HTTP cache can serve the image instantly
 *      on subsequent mounts.
 */
const CACHE_PREFIX = 'asset_cache_v1:';
const CACHE_TTL_MS = 1000 * 60 * 60 * 24; // 24 hours

const memoryCache = new Map<string, string>();
const pendingFetches = new Map<string, Promise<string | null>>();

type CachedEntry = {
  url: string;
  cachedAt: number;
};

const isStorageAvailable = (): boolean => {
  try {
    const testKey = '__asset_cache_test__';
    window.localStorage.setItem(testKey, 'ok');
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = typeof window !== 'undefined' && isStorageAvailable();

const readFromStorage = (path: string): string | null => {
  if (!storageAvailable) return null;
  try {
    const raw = window.localStorage.getItem(`${CACHE_PREFIX}${path}`);
    if (!raw) return null;
    const parsed: CachedEntry = JSON.parse(raw);
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) {
      window.localStorage.removeItem(`${CACHE_PREFIX}${path}`);
      return null;
    }
    return parsed.url;
  } catch {
    return null;
  }
};

const writeToStorage = (path: string, url: string) => {
  if (!storageAvailable) return;
  try {
    const entry: CachedEntry = { url, cachedAt: Date.now() };
    window.localStorage.setItem(`${CACHE_PREFIX}${path}`, JSON.stringify(entry));
  } catch {
    // Ignore quota/serialization errors
  }
};

export const getCachedAssetUrl = async (
  path: string,
  loader: () => Promise<string | null>
): Promise<string | null> => {
  if (!path) return null;

  if (memoryCache.has(path)) {
    return memoryCache.get(path)!;
  }

  const stored = readFromStorage(path);
  if (stored) {
    memoryCache.set(path, stored);
    return stored;
  }

  const existingFetch = pendingFetches.get(path);
  if (existingFetch) {
    return existingFetch;
  }

  const fetchPromise = loader()
    .then((url) => {
      if (url) {
        memoryCache.set(path, url);
        writeToStorage(path, url);
      }
      pendingFetches.delete(path);
      return url;
    })
    .catch((error) => {
      pendingFetches.delete(path);
      throw error;
    });

  pendingFetches.set(path, fetchPromise);
  return fetchPromise;
};

export const clearCachedAsset = (path: string) => {
  memoryCache.delete(path);
  if (storageAvailable) {
    window.localStorage.removeItem(`${CACHE_PREFIX}${path}`);
  }
};
